/**
 * "Follow the road" (TD §5 followRoad): while a road's dots are dragged or its width changes, nearby unlocked
 * habitat corners, pools and free icons are carried along. Ported from the prototype's makeFollow/applyFollow
 * and makeWidthPush, merged into one field:
 * - every point gets a weight: 1 next to the road, fading to 0 at 70 m, less where another road is closer;
 * - it moves by the weighted displacement of the nearest stretch of road, plus the weighted change of the
 *   road's half width there, outward on its own side.
 * Weights depend only on position, and shared corners are one object, so shared walls stay joined (same ids).
 */
import type { IconId, NodeId, PlanDoc, Pt, RoadId, VertId } from '../../shared/types';
import { clamp, distPoly, lerpAt, nearestOnPoly, spreadAlong } from '../geom/basic';
import { derive } from '../geom/derive';
import { roadPoly } from '../geom/shapes';
import { isAnimalArea } from '../model/query';

/** Distance (m) at which follow fades out completely. */
export const FOLLOW_RANGE_M = 70;
/** Beyond half the road width, points within this margin (m) still move fully. */
export const FOLLOW_FULL_MARGIN_M = 6;

/** One point that follows: where it was at drag start, its weight and the spot on the road it hangs from. */
export interface FollowItem {
  kind: 'vert' | 'icon';
  id: VertId | IconId;
  x: number;
  y: number;
  /** 0..1 */
  w: number;
  /** Segment index and fraction on the road's centre line at drag start. */
  i: number;
  t: number;
  /** Unit normal of that segment, pointing to the point's side of the road. */
  nx: number;
  ny: number;
}

/** Captured at drag start by prepareFollow; applyFollow uses it on every drag step. */
export interface FollowCtx {
  roadId: RoadId;
  mode: 'move' | 'width';
  old: Pt[];
  oldWidths: number[];
  items: FollowItem[];
}

/**
 * Corners that may follow: those of unlocked animal areas and of pools (whose habitat isn't locked),
 * but not corners also used by a locked area, a lake, plaza, back-of-house zone or the boundary.
 */
function followableVerts(doc: PlanDoc): Set<VertId> {
  const yes = new Set<VertId>(), no = new Set<VertId>(doc.plot?.verts ?? []);
  for (const a of Object.values(doc.areas)) {
    let movable = false;
    if (isAnimalArea(a)) movable = !a.locked;
    else if (a.kind === 'pool') {
      const p = a.parent !== undefined ? doc.areas[a.parent] : undefined;
      movable = !(p && isAnimalArea(p) && p.locked);
    }
    for (const v of a.verts) (movable ? yes : no).add(v);
  }
  for (const v of no) yes.delete(v);
  return yes;
}

/**
 * Captures, from the doc at drag start, which points follow the road and how strongly (pure).
 * `mode: 'width'` with a `nodeId` only takes points near the stretch that dot's width affects.
 */
export function prepareFollow(doc: PlanDoc, roadId: RoadId, mode: 'move' | 'width', nodeId?: NodeId): FollowCtx {
  const derived = derive(doc), road = doc.roads[roadId], poly = derived.roadPolys[roadId];
  const ctx: FollowCtx = { roadId, mode, old: poly ? poly.pts.map(p => [p[0], p[1]]) : [], oldWidths: poly ? poly.widths.slice() : [], items: [] };
  if (!road || !poly || poly.pts.length < 2) return ctx;
  const old = ctx.old, R = FOLLOW_RANGE_M, r0 = Math.max(...poly.widths) / 2 + FOLLOW_FULL_MARGIN_M;
  const others = Object.entries(derived.roadPolys).filter(([id]) => id !== roadId).map(([, p]) => p.pts);
  let infl: number[] | null = null;
  if (mode === 'width' && nodeId !== undefined) {
    const closed = road.closed && road.nodes.length >= 3;
    infl = spreadAlong(road.nodes.map(n => (n === nodeId ? 1 : 0)), closed, old.length);
  }
  const add = (kind: FollowItem['kind'], id: string, x: number, y: number) => {
    const q = nearestOnPoly(old, x, y);
    if (q.d >= R) return;
    if (infl && lerpAt(infl, q.i, q.t) < 0.01) return;
    const s = clamp((q.d - r0) / (R - r0), 0, 1);
    let w = 1 - s * s * (3 - 2 * s);
    let dO = 1e9;
    for (const p of others) dO = Math.min(dO, distPoly(p, x, y, false));
    if (dO < q.d) w *= dO / Math.max(q.d, 0.01);
    const side = Math.sign((x - q.px) * q.nx + (y - q.py) * q.ny) || 1;
    if (w > 0.002) ctx.items.push({ kind, id, x, y, w, i: q.i, t: q.t, nx: q.nx * side, ny: q.ny * side });
  };
  for (const vid of followableVerts(doc)) { const v = doc.verts[vid]; if (v) add('vert', vid, v.x, v.y); }
  for (const [iid, ic] of Object.entries(doc.icons)) if (ic.type !== 'entrance' && !ic.fixed) add('icon', iid, ic.x, ic.y);
  return ctx;
}

/**
 * Moves the captured points after the road's dots or widths changed in the draft. Positions are set from the
 * drag-start state, so calling it on every drag step (with the dots set absolutely) never drifts.
 * Does nothing if the road's dot count changed since prepareFollow.
 */
export function applyFollow(draft: PlanDoc, ctx: FollowCtx): void {
  const road = draft.roads[ctx.roadId];
  if (!road || !ctx.items.length) return;
  const { pts: nw, widths: nwW } = roadPoly(draft, road), od = ctx.old;
  if (nw.length !== od.length) return;
  for (const it of ctx.items) {
    const j = Math.min(it.i + 1, od.length - 1);
    const a = od[it.i] as Pt, b = od[j] as Pt, na = nw[it.i] as Pt, nb = nw[j] as Pt, t = it.t;
    const dx = (na[0] - a[0]) * (1 - t) + (nb[0] - b[0]) * t, dy = (na[1] - a[1]) * (1 - t) + (nb[1] - b[1]) * t;
    const dHalf = (lerpAt(nwW, it.i, t) - lerpAt(ctx.oldWidths, it.i, t)) / 2;
    const x = it.x + it.w * (dx + it.nx * dHalf), y = it.y + it.w * (dy + it.ny * dHalf);
    const target = it.kind === 'vert' ? draft.verts[it.id] : draft.icons[it.id];
    if (target) { target.x = x; target.y = y; }
  }
}

/** TD §5's name for applyFollow. */
export const followRoad = applyFollow;
